"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { REALISATIONS, localizeRealisation, realisationPath } from "@/lib/content/realisations-bank";
import type { OfferLanguage } from "@/db/schema/content-items";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";

const ARROW_BASE_CLASS =
  "border-ink/15 bg-paper-pure text-ink shadow-sm hover:border-gold/50 hover:text-gold disabled:opacity-25";

// Slider de mini-cartes "étude de cas", affiché juste avant le titre de la
// section "enjeux" (stakes-mockups.tsx) : preuve sociale rapide avant même
// que la page n'entre dans le détail. Chaque carte (logo, titre, enjeu) est
// un lien vers l'étude de cas complète du projet correspondant (source
// unique : realisations-bank.ts, aucune donnée dupliquée ici). Logos dans
// leurs couleurs d'origine, sans retraitement.
//
// Pas de fond : un simple liseré doré délimite le bloc, contenu (pas pleine
// largeur) comme le panneau du mot de page juste au-dessus. Le trait vertical
// en haut relie visuellement les deux blocs entre eux.
export function CaseStudyLogosMarquee({
  excludeId,
  lang = "fr",
}: { excludeId?: string; lang?: OfferLanguage } = {}) {
  const PROJECTS = REALISATIONS.filter((r) => r.logoPath && r.id !== excludeId).map((r) =>
    localizeRealisation(r, lang)
  );
  if (PROJECTS.length === 0) return null;

  return (
    <div className="relative mx-auto max-w-4xl rounded-3xl border border-gold/50 px-7 py-10 sm:px-14 sm:py-12">
      <span
        aria-hidden
        className="absolute left-1/2 bottom-full h-10 w-px -translate-x-1/2 bg-gradient-to-b from-transparent to-gold/60 sm:h-14"
      />

      <div className="relative mx-auto flex max-w-md items-center justify-center gap-4">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-gold/60" />
        <Link
          href={lang === "en" ? "/en/realisations" : "/realisations"}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gold/40 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-gold-muted transition-colors hover:border-gold hover:text-gold"
        >
          {lang === "en" ? "Other case studies" : "Autres études de cas"}
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-gold/60" />
      </div>

      <Carousel opts={{ align: "start" }} className="mt-8">
        <CarouselContent className="-ml-4">
          {PROJECTS.map((r) => (
            <CarouselItem key={r.id} className="pl-4 sm:basis-1/2">
              <Link
                href={realisationPath(r, lang)}
                className="group flex h-full flex-col gap-3 rounded-2xl border border-ink/10 p-5 transition-colors duration-300 hover:border-gold/50"
              >
                <Image
                  src={r.logoPath!}
                  alt={r.name}
                  width={156}
                  height={48}
                  className="h-7 w-auto object-contain"
                />
                <h3 className="font-display text-xl leading-snug text-ink">{r.title}</h3>
                <p className="font-display text-[19px] italic leading-relaxed text-gold-muted">
                  {r.summary}
                </p>
                <span className="mt-auto flex items-center gap-1 pt-1 font-mono text-[11px] uppercase tracking-widest text-navy transition-colors group-hover:text-gold">
                  {lang === "en" ? "Case study" : "Étude de cas"}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious className={`-left-3 sm:-left-12 ${ARROW_BASE_CLASS}`} />
        <CarouselNext className={`-right-3 sm:-right-12 ${ARROW_BASE_CLASS}`} />
      </Carousel>
    </div>
  );
}
